import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useId, useState } from "react";
import { toast } from "sonner";
import { Check } from "@/components/animate-ui/icons/check";
import { Loader } from "@/components/animate-ui/icons/loader";
import { AuthShell } from "@/components/auth/auth-shell";
import { ResendVerificationButton } from "@/components/resend-verification-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useFormValidation } from "@/hooks/use-form-validation";
import { apiClient } from "@/lib/api/client";
import { useRedirectIfAuthenticated } from "@/lib/auth/hooks";
import { buildLinks, buildMetaTags, getCanonical } from "@/lib/seo/meta";
import { verifyEmailSchema } from "@/lib/validation/auth";
import { useAuthStore } from "@/stores/auth-store";

export const Route = createFileRoute("/verify-email")({
	// Redirect authenticated users to dashboard
	beforeLoad: ({ search }) => {
		if (typeof window === "undefined") {
			return;
		}

		const { isAuthenticated } = useAuthStore.getState();

		if (isAuthenticated) {
			const redirectTo = (search as { redirect?: string })?.redirect || "/";
			throw redirect({ to: redirectTo });
		}
	},

	head: () => {
		const meta = buildMetaTags({
			title: "Verify Email",
			description: "Verify your m8tes.ai email address",
			noIndex: true,
			noFollow: true,
		});

		const links = buildLinks({
			canonical: getCanonical("/verify-email"),
		});

		return { meta, links };
	},

	component: VerifyEmailPage,
});

export function VerifyEmailPage() {
	useRedirectIfAuthenticated("/");

	const navigate = useNavigate();
	const emailId = useId();
	const [token, setToken] = useState<string | null>(null);
	const [email, setEmail] = useState("");
	const [isVerifying, setIsVerifying] = useState(false);
	const [isVerified, setIsVerified] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const { setAuth } = useAuthStore();

	// Form validation
	const validation = useFormValidation(verifyEmailSchema);

	const verifyWithToken = useCallback(
		async (verificationToken: string) => {
			setIsVerifying(true);
			setError(null);

			try {
				const { data, error } = await apiClient.POST("/api/v1/auth/verify-email", {
					body: { token: verificationToken },
				});

				if (error || !data) {
					throw new Error(typeof error?.detail === "string" ? error.detail : "Verification failed");
				}

				// Set auth state with tokens
				setAuth({
					user: data.user,
					accessToken: data.api_key,
					refreshToken: data.refresh_token,
					accessExpiresAt: data.access_expires_at || "",
					refreshExpiresAt: data.refresh_expires_at || "",
				});

				setIsVerified(true);
				toast.success("Email verified successfully!");

				// Redirect to dashboard after short delay
				setTimeout(() => {
					navigate({ to: "/" });
				}, 2000);
			} catch (err) {
				const errorMessage = err instanceof Error ? err.message : "Verification failed";
				setError(errorMessage);
				toast.error(errorMessage);
			} finally {
				setIsVerifying(false);
			}
		},
		[navigate, setAuth]
	);

	// Extract token from URL on mount
	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const urlToken = urlParams.get("token");

		if (urlToken) {
			setToken(urlToken);
			// Auto-verify if token is in URL
			verifyWithToken(urlToken);
		}
	}, [verifyWithToken]);

	// Auto-verifying from URL token
	if (token && isVerifying) {
		return (
			<AuthShell>
				<div className="flex flex-col gap-4 text-center">
					<div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">
						<Loader className="size-6" animateOnHover />
					</div>
					<div className="flex flex-col gap-2">
						<h1 className="text-2xl font-semibold tracking-tight">Verifying your email</h1>
						<p className="text-sm text-muted-foreground">
							Please wait while we verify your account...
						</p>
					</div>
				</div>
			</AuthShell>
		);
	}

	// Verified successfully
	if (isVerified) {
		return (
			<AuthShell>
				<div className="flex flex-col gap-4 text-center">
					<div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent-cyan-subtle text-accent-cyan">
						<Check className="size-6" animateOnHover />
					</div>
					<div className="flex flex-col gap-2">
						<h1 className="text-2xl font-semibold tracking-tight">Email verified!</h1>
						<p className="text-sm text-muted-foreground">
							Your email has been successfully verified. Redirecting you to your dashboard...
						</p>
					</div>
				</div>
			</AuthShell>
		);
	}

	// Link-based verification flow with resend support
	return (
		<AuthShell>
			<div className="flex flex-col gap-2 text-center">
				<h1 className="text-2xl font-semibold tracking-tight">Verify your email</h1>
				<p className="text-sm text-muted-foreground">
					Click the verification link in your email to complete verification.
				</p>
			</div>

			{error && (
				<div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
			)}

			<div className="flex flex-col gap-4">
				<div className="flex flex-col gap-2">
					<Label htmlFor={emailId}>Email address</Label>
					<Input
						id={emailId}
						type="email"
						placeholder="name@example.com"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						onBlur={() => validation.validateField("email", email, { email })}
						required
						autoComplete="email"
						autoFocus
					/>
					{validation.getFieldError("email") ? (
						<p className="text-xs text-destructive">{validation.getFieldError("email")}</p>
					) : (
						<p className="text-xs text-muted-foreground">
							Enter your email to resend the verification link
						</p>
					)}
				</div>
			</div>

			<div className="text-center text-sm text-muted-foreground">
				<p className="mb-3">Didn't receive the email?</p>
				<ResendVerificationButton email={email} variant="outline" className="w-full" />
			</div>
		</AuthShell>
	);
}
